import { Component, OnDestroy, OnInit } from '@angular/core';
import { OfficineSelectorComponent } from '../shared/components/officine-selector/officine-selector.component';
import { MatToolbar } from '@angular/material/toolbar';
import { CommonModule } from '@angular/common';
import {
  Subject,
  BehaviorSubject,
  takeUntil,
  tap,
  combineLatest,
  filter,
  switchMap,
  retry,
  catchError,
  of,
  map,
  distinctUntilChanged,
} from 'rxjs';
import { Adherent } from '../shared/interfaces/BDD-Ref/adherent';
import { SnackbarService } from '../shared/services/snackbar/snackbar.service';
import { UserSessionService } from '../shared/services/user.service';
import { WholesalerTableComponent } from './components/wholesaler-table/wholesaler-table.component';
import { YearsSelectorComponent } from '../shared/components/years-selector/years-selector.component';
import { WholesalerSimulationService } from '../shared/services/wholesaler-simulation.service';
import { SimulationsBySupplier } from '../shared/objects/simulations-by-supplier';
import { WholesalerSimulation } from '../shared/objects/wholesaler-simulation';
import { WholesalerSimulationGlobalInformation } from '../shared/objects/wholesaler-simulation-global-information';
import { MonthsSelectorComponent } from '../shared/components/months-selector/months-selector.component';
import { ChangeDetectorRef } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { FilialeSelectorComponent } from '../shared/components/filiale-selector/filiale-selector.component';
@Component({
  selector: 'app-wholesaler-simulation',
  templateUrl: './wholesaler-simulation.component.html',
  styleUrl: './wholesaler-simulation.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    OfficineSelectorComponent,
    FilialeSelectorComponent,
    YearsSelectorComponent,
    MatToolbar,
    WholesalerTableComponent,
    MonthsSelectorComponent,
    MatTabsModule,
  ],
})
export class WholesalerSimulationComponent implements OnInit, OnDestroy {
  private readonly _destroying$: Subject<void> = new Subject<void>();

  readonly selectedMonths$ = new BehaviorSubject<string[] | undefined>(undefined);
  readonly selectedYear$ = new BehaviorSubject<number | undefined>(undefined);
  readonly adherent$ = new BehaviorSubject<Adherent | undefined>(undefined);
  readonly wholesalerSimulations$ = new BehaviorSubject<
    SimulationsBySupplier<WholesalerSimulation, WholesalerSimulationGlobalInformation>[]
  >([]);
  availableYears: number[] | undefined = [];
  availableMonths: string[] | undefined = [];
  private _wholesalerBackup: any[] = [];
   activiteAdherent!:string;

  constructor(
    private readonly _userService: UserSessionService,
    private readonly _wholesalerSimulationService: WholesalerSimulationService,
    private readonly _snackBarService: SnackbarService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this._userService
      .getSelectedAdherent()
      .pipe(
        distinctUntilChanged((a, b) => a === b),
        takeUntil(this._destroying$),
        tap((adherent: Adherent | null) => {
          if (adherent) {
            this.setAdherent(adherent);
            this.initializeMonthsAndYears(adherent);
          }
        }),
      )
      .subscribe();

    combineLatest([this.adherent$, this.selectedYear$])
      .pipe(
        distinctUntilChanged(
          ([aAdh, aYear], [bAdh, bYear]) =>
            (aAdh?.idReferentiel ?? null) === (bAdh?.idReferentiel ?? null) && aYear === bYear
        ),
        filter(([adherent]) => !!adherent && adherent.idReferentiel > 0),
        switchMap(([adherent, year]) => {
          const id = adherent!.idReferentiel;

          const request$ =
            year && year > 0
              ? this._wholesalerSimulationService.getWholesalerSimulationByAdherentAndYear(id, year)
              : this._wholesalerSimulationService.getWholesalerSimulationByAdherent(id);

          return request$.pipe(
            catchError(error => {
              console.error('Erreur lors de la récupération des remises grossistes :', error);
              this._snackBarService.show(
                error?.error ?? 'Une erreur est survenue. Veuillez réessayer plus tard.',
                'snackbar-error',
              );
              return of([]);
            }),
          );
        }),
        tap((response: any[]) => {
          // ✅ Backup pour les mois futurs
          this._wholesalerBackup = response;
        }),
        switchMap(() => this.selectedMonths$), // ⏳ on attend le changement de mois
        map(selectedMonths => {
          if (!Array.isArray(this._wholesalerBackup)) return [];

          return (
            this._wholesalerBackup
              .map((item: any) => {
                // Étape 1 : filtrer les simulations par mois sélectionnés
                const filteredSimulations = item.simulations.filter((sim: any) =>
                  selectedMonths?.some(m => m.startsWith(sim.mois?.toString() + ' - ')),
                );
                // Étape 1.1 : filtrer les simulations Globales par mois sélectionnés
                const filteredSimulationGlobalInformation = item.simulationGlobalInformation.filter(
                  (info: any) =>
                    selectedMonths?.some(m => m.startsWith(info.mois?.toString() + ' - ')) &&
                    info.annee === this.selectedYear$.value,
                );

                // Étape 2 : regrouper par `assiette` (base)
                const groupedMap: Record<string, any> = {};

                for (const sim of filteredSimulations) {
                  const baseKey = sim.assiette || 'UNKNOWN';

                  if (!groupedMap[baseKey]) {
                    // Initialisation avec copie du premier élément
                    groupedMap[baseKey] = {
                      ...sim,
                      quantite: sim.quantite || 0,
                      montantBrutHt: sim.montantBrutHt || 0,
                      montantBrutHT_PF: sim.montantBrutHT_PF || 0,
                      remiseEuroHt: sim.remiseEuroHt || 0,
                      montantNetHt: sim.montantNetHt || 0,
                    };
                  } else {
                    // Cumul des champs numériques
                    groupedMap[baseKey].quantite += sim.quantite || 0;
                    groupedMap[baseKey].montantBrutHt += sim.montantBrutHt || 0;
                    groupedMap[baseKey].montantBrutHT_PF += sim.montantBrutHT_PF || 0;
                    groupedMap[baseKey].remiseEuroHt += sim.remiseEuroHt || 0;
                    groupedMap[baseKey].montantNetHt += sim.montantNetHt || 0;
                  }
                }

                const groupedSimulations = Object.values(groupedMap);

                return {
                  id: crypto.randomUUID(),
                  supplierName: item.simulations?.[0]?.nomGrossiste ?? 'Inconnu',
                  simulations: WholesalerSimulation.fromBackendList(groupedSimulations),
                  simulationGlobalInformation:
                    WholesalerSimulationGlobalInformation.fromBackendList(
                      filteredSimulationGlobalInformation,
                    ),
                };
              })
              // Ajoute ce filtre pour ne rien renvoyer si les deux tableaux sont vides (pour afficher Aucune remise générique/grossiste trouvée
              .filter(
                (obj: any) =>
                  (obj.simulations && obj.simulations.length > 0) ||
                  (obj.simulationGlobalInformation && obj.simulationGlobalInformation.length > 0),
              )
          );
        }),

        tap(sorted => {
          this.wholesalerSimulations$.next(sorted);
        }),
        takeUntil(this._destroying$),
      )
      .subscribe();
      this.activiteAdherent = this._userService.getActivite();
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  trackingKey(
    index: number,
    simulation: SimulationsBySupplier<WholesalerSimulation, WholesalerSimulationGlobalInformation>,
  ): string {
    return simulation.id;
  }

  onYearSelectionChange(value: number): void {
    this.selectedYear$.next(value);

    const currentYear = new Date().getFullYear();
    const currentMonthIndex = new Date().getMonth();

    this.getAvailableMonths(); // met à jour this.availableMonths

    // si pas de mois disponibles
    if (!this.availableMonths || this.availableMonths.length === 0) {
      return;
    }

    if (value === currentYear) {
      // année en cours > seulement le dernier mois
      const lastMonth = this.availableMonths[currentMonthIndex - 1];
      // uniquement si lastMonth est défini
      if (lastMonth) {
        this.selectedMonths$.next([lastMonth]);
        this.cd.detectChanges();
      }
    } else {
      // autre année > tous les mois
      this.selectedMonths$.next(Array.isArray(this.availableMonths) ? [...this.availableMonths] : []);
      this.cd.detectChanges();
    }
  }

  onMonthSelectionChange(value: any): void {
    this.selectedMonths$.next(value);
    this.cd.detectChanges();
  }

  // 1
  private setAdherent(adherent: Adherent): void {
    // Forcer la réinitialisation des mois
    this.adherent$.next(adherent);
    this.cd.detectChanges();
  }

  private initializeMonthsAndYears(adherent: Adherent): void {
    this._wholesalerSimulationService
      .getAvailableYearsByAdherent(adherent.idReferentiel)
      .pipe(
      distinctUntilChanged((a, b) => Array.isArray(a) && Array.isArray(b) ? a.join() === b.join() : a === b),
        takeUntil(this._destroying$),
        tap((years: number[]) => {
          const hasYears = years && years.length > 0;
          const listOfYears = hasYears ? years : undefined;
          const isJanuary = new Date().getMonth() === 0;
          let yearToUse: number | undefined = undefined;
          if (hasYears) {
            if (isJanuary && years.length > 1) {
              // Prendre l'année la plus récente qui est < année courante
              yearToUse = Math.max(...years.filter(y => y < new Date().getFullYear()));
            } else {
              yearToUse = Math.max(...years);
            }
          }

          this.availableYears = listOfYears;
          this.getAvailableMonths();
          this.selectedYear$.next(yearToUse);
        this.onYearSelectionChange(yearToUse!);
        }),
      )
      .subscribe();
  }

  private getAvailableMonths(): void {
    var i: number = 1;
    this.availableMonths = [];
    const currentYear = new Date().getFullYear();
    // Si on est en cours d'année, on limite les mois à ceux déjà passés
    while (i <= (this.selectedYear$.value == currentYear ? new Date().getMonth() : 12)) {
      this.availableMonths!.push(
        i.toString() + ' - ' + new Date(0, i - 1).toLocaleString('fr', { month: 'long' }),
      );
      i++;
    }
  }
}
